import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

type OrderListItem = {
  billNo: string;
  paymentMethod: string | null;
  saleChannel: string | null;
  recDateBE: string | null;
  recTime: string | null;
  totalItems: number;
  orderTotal: number;
  afterDiscount: number | null;
  orderCreated: string | null;
};

type OrdersListResponse = {
  items?: unknown;
  total?: unknown;
  page?: unknown;
  limit?: unknown;
  error?: string;
};

function toNumber(value: unknown): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function toOrderListItem(row: unknown): OrderListItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const billNo = typeof data.billNo === "string" ? data.billNo.trim() : "";
  if (!billNo) return null;

  return {
    billNo,
    paymentMethod: typeof data.paymentMethod === "string" ? data.paymentMethod : null,
    saleChannel: typeof data.saleChannel === "string" ? data.saleChannel : null,
    recDateBE: typeof data.recDateBE === "string" ? data.recDateBE : null,
    recTime: typeof data.recTime === "string" ? data.recTime : null,
    totalItems: toNumber(data.totalItems),
    orderTotal: toNumber(data.orderTotal),
    afterDiscount: data.afterDiscount == null ? null : toNumber(data.afterDiscount),
    orderCreated: typeof data.orderCreated === "string" ? data.orderCreated : null,
  };
}

function formatCurrency(value: number): string {
  return value.toLocaleString("th-TH", {
    style: "currency",
    currency: "THB",
  });
}

// PMN-CRITICAL(/OrdersDateEditPage): Date display is dd/mm/yyyy, but saved state/storage keeps the BE year in yyyy-mm-dd; do not convert it to CE here.
function formatBEDisplayDate(value: string | null): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    return trimmed;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(trimmed);
  if (!match) return trimmed;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

function parseBEDisplayDate(value: string): string | null {
  const trimmed = value.trim();
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
  if (!match) return null;

  const [, dayStr, monthStr, yearStr] = match;
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);

  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
    return null;
  }

  const iso = `${yearStr}-${monthStr}-${dayStr}`;
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getFullYear() !== year) return null;
  if (date.getMonth() + 1 !== month) return null;
  if (date.getDate() !== day) return null;
  return iso;
}

type DateInputProps = {
  value: string;
  onChange: (value: string) => void;
};

function DateInput({ value, onChange }: DateInputProps): JSX.Element {
  const [displayValue, setDisplayValue] = useState(() => formatBEDisplayDate(value));

  useEffect(() => {
    setDisplayValue(formatBEDisplayDate(value));
  }, [value]);

  return (
    <input
      type="text"
      inputMode="numeric"
      className="form-control"
      placeholder="dd/mm/yyyy"
      value={displayValue}
      onChange={(e) => {
        const next = e.target.value;
        setDisplayValue(next);
        if (!next.trim()) {
          onChange("");
          return;
        }
        const parsed = parseBEDisplayDate(next);
        if (parsed) {
          onChange(parsed);
        }
      }}
      onBlur={() => {
        if (!displayValue.trim()) {
          setDisplayValue("");
          return;
        }
        const parsed = parseBEDisplayDate(displayValue);
        setDisplayValue(parsed ? formatBEDisplayDate(parsed) : formatBEDisplayDate(value));
      }}
    />
  );
}

const ORDERS_LIST_API = apiUrl("/orders-list");
const PAGE_SIZE = 100;

export default function OrdersDateEditPage(): JSX.Element {
  const [items, setItems] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [dateDrafts, setDateDrafts] = useState<Record<string, string>>({});
  const [savingBillNo, setSavingBillNo] = useState<string | null>(null);
  const [selectedBillNos, setSelectedBillNos] = useState<string[]>([]);
  const [bulkDate, setBulkDate] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    void loadOrders(query, currentPage);
  }, [query, currentPage]);

  async function loadOrders(nextQuery: string, nextPage: number): Promise<void> {
    setLoading(true);
    setError("");

    try {
      const url = new URL(ORDERS_LIST_API);
      url.searchParams.set("page", String(nextPage));
      url.searchParams.set("limit", String(PAGE_SIZE));
      if (nextQuery.trim()) {
        url.searchParams.set("q", nextQuery.trim());
      }

      const resp = await fetch(url.toString());
      const data = (await resp.json().catch(() => ({}))) as OrdersListResponse;
      if (!resp.ok || !Array.isArray(data.items)) {
        throw new Error(data.error ?? "failed_to_fetch_orders");
      }

      const nextItems = data.items
        .map(toOrderListItem)
        .filter((item): item is OrderListItem => item != null);

      setItems(nextItems);
      setTotalItemsCount(toNumber(data.total));
      setDateDrafts(
        nextItems.reduce<Record<string, string>>((acc, item) => {
          acc[item.billNo] = item.recDateBE ?? "";
          return acc;
        }, {})
      );
      setSelectedBillNos([]);
      setError("");
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_fetch_orders";
      setError(code === "failed_to_fetch_orders" ? "โหลดรายการบิลไม่สำเร็จ" : code);
    } finally {
      setLoading(false);
    }
  }

  // PMN-CRITICAL(/OrdersDateEditPage): Single save must update only orders.rec_date_be for this bill; totals, payment, items, and stock are outside this page's contract.
  async function handleSave(item: OrderListItem): Promise<void> {
    const nextDate = dateDrafts[item.billNo] ?? "";
    if (!nextDate) {
      setError("กรุณาใส่วันที่ใหม่ให้ครบ");
      return;
    }

    setSavingBillNo(item.billNo);
    setError("");
    setSuccessMessage("");

    try {
      const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(item.billNo)}/rec-date`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rec_date_be: formatBEDisplayDate(nextDate),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        throw new Error(data?.error ?? "failed_to_update_order_date");
      }

      setItems((prev) =>
        prev.map((row) =>
          row.billNo === item.billNo
            ? {
                ...row,
                recDateBE: typeof data.rec_date_be === "string" ? data.rec_date_be : nextDate,
              }
            : row
        )
      );
      setDateDrafts((prev) => ({
        ...prev,
        [item.billNo]: typeof data.rec_date_be === "string" ? data.rec_date_be : nextDate,
      }));
      setSuccessMessage(`อัปเดตวันที่ของบิล ${item.billNo} เรียบร้อย`);
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_update_order_date";
      switch (code) {
        case "bill_no_required":
          setError("ไม่พบเลขบิล");
          break;
        case "rec_date_be_required":
          setError("กรุณาใส่วันที่ใหม่");
          break;
        case "invalid_rec_date_be":
          setError("รูปแบบวันที่ต้องเป็น dd/mm/yyyy");
          break;
        case "order_not_found":
          setError("ไม่พบบิลที่ต้องการแก้ไข");
          break;
        default:
          setError("อัปเดตวันที่บิลไม่สำเร็จ");
      }
    } finally {
      setSavingBillNo(null);
    }
  }

  async function patchOrderDate(billNo: string, nextDate: string): Promise<string> {
    const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(billNo)}/rec-date`), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        rec_date_be: formatBEDisplayDate(nextDate),
      }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data?.ok) {
      throw new Error(data?.error ?? "failed_to_update_order_date");
    }
    return typeof data.rec_date_be === "string" ? data.rec_date_be : nextDate;
  }

  // PMN-CRITICAL(/OrdersDateEditPage): Bulk save sends one PATCH per bill, so a later failure can leave earlier bills already updated in the DB.
  async function handleBulkSave(): Promise<void> {
    if (selectedBillNos.length === 0) {
      setError("กรุณาเลือกบิลที่ต้องการแก้ไข");
      return;
    }
    if (!bulkDate) {
      setError("กรุณาใส่วันที่ใหม่สำหรับบิลที่เลือก");
      return;
    }

    setBulkSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const updatedMap = new Map<string, string>();

      for (const billNo of selectedBillNos) {
        const nextDate = await patchOrderDate(billNo, bulkDate);
        updatedMap.set(billNo, nextDate);
      }

      setItems((prev) =>
        prev.map((row) =>
          updatedMap.has(row.billNo)
            ? {
                ...row,
                recDateBE: updatedMap.get(row.billNo) ?? row.recDateBE,
              }
            : row
        )
      );
      setDateDrafts((prev) => {
        const next = { ...prev };
        updatedMap.forEach((value, key) => {
          next[key] = value;
        });
        return next;
      });
      setSuccessMessage(`อัปเดตวันที่ ${updatedMap.size} บิลเรียบร้อย`);
      setSelectedBillNos([]);
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_update_order_date";
      switch (code) {
        case "bill_no_required":
          setError("ไม่พบเลขบิล");
          break;
        case "rec_date_be_required":
          setError("กรุณาใส่วันที่ใหม่");
          break;
        case "invalid_rec_date_be":
          setError("รูปแบบวันที่ต้องเป็น dd/mm/yyyy");
          break;
        case "order_not_found":
          setError("ไม่พบบิลที่ต้องการแก้ไข");
          break;
        default:
          setError("อัปเดตวันที่หลายบิลไม่สำเร็จ");
      }
    } finally {
      setBulkSaving(false);
    }
  }

  const totalVisibleAmount = useMemo(
    () => items.reduce((sum, item) => sum + (item.afterDiscount ?? item.orderTotal), 0),
    [items]
  );
  const totalPages = Math.max(1, Math.ceil(totalItemsCount / PAGE_SIZE));
  const startItem = totalItemsCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = totalItemsCount === 0 ? 0 : Math.min(currentPage * PAGE_SIZE, totalItemsCount);
  const allSelected = items.length > 0 && selectedBillNos.length === items.length;

  return (
    <div className="min-vh-100 bg-primary py-4 py-md-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <h2 className="text-primary mb-1">แก้วันที่บิล</h2>
                    <div className="text-muted">
                      ใช้สำหรับแก้วันที่ย้อนหลังของบิลที่ลงผิดวัน โดยไม่แตะยอดขายหรือรายการสินค้า
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => void loadOrders(query, currentPage)}
                      disabled={loading}
                    >
                      {loading ? "กำลังโหลด..." : "รีเฟรช"}
                    </button>
                    <Link to="/" className="btn btn-outline-primary">
                      กลับหน้าเมนู
                    </Link>
                  </div>
                </div>

                <form
                  className="row g-2 mb-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setCurrentPage(1);
                    setQuery(queryInput.trim());
                  }}
                >
                  <div className="col-12 col-md-8">
                    <input
                      type="search"
                      className="form-control"
                      placeholder="ค้นหาเลขบิล"
                      value={queryInput}
                      onChange={(e) => setQueryInput(e.target.value)}
                    />
                  </div>
                  <div className="col-6 col-md-2">
                    <button type="submit" className="btn btn-primary w-100">
                      ค้นหา
                    </button>
                  </div>
                  <div className="col-6 col-md-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100"
                      onClick={() => {
                        setQueryInput("");
                        setCurrentPage(1);
                        setQuery("");
                      }}
                    >
                      ล้าง
                    </button>
                  </div>
                </form>

                <div className="small text-muted mb-3">
                  แสดง {startItem}-{endItem} จากทั้งหมด {totalItemsCount} บิล มูลค่ารวม{" "}
                  <span className="fw-semibold">{formatCurrency(totalVisibleAmount)}</span>
                </div>

                <div className="card border-0 bg-light rounded-4 mb-3">
                  <div className="card-body p-3">
                    <div className="d-flex flex-column flex-md-row align-items-md-end gap-3">
                      <div className="flex-grow-1">
                        <div className="small text-muted mb-1">วันที่ใหม่สำหรับบิลที่เลือก</div>
                        <DateInput value={bulkDate} onChange={setBulkDate} />
                      </div>
                      <div className="d-flex flex-column gap-2">
                        <div className="small text-muted">
                          เลือกแล้ว <span className="fw-semibold">{selectedBillNos.length}</span> บิล
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={bulkSaving || selectedBillNos.length === 0}
                          onClick={() => void handleBulkSave()}
                        >
                          {bulkSaving ? "กำลังบันทึกหลายบิล..." : "บันทึกวันที่ให้บิลที่เลือก"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {error && <div className="alert alert-danger rounded-3">{error}</div>}
                {successMessage && <div className="alert alert-success rounded-3">{successMessage}</div>}

                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 56 }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={allSelected}
                            onChange={(e) => {
                              setSelectedBillNos(e.target.checked ? items.map((item) => item.billNo) : []);
                            }}
                            aria-label="เลือกทั้งหมด"
                          />
                        </th>
                        <th style={{ width: 120 }}>Bill No</th>
                        <th style={{ width: 150 }}>วันที่ปัจจุบัน</th>
                        <th style={{ width: 190 }}>วันที่ใหม่</th>
                        <th style={{ width: 140 }}>Payment</th>
                        <th style={{ width: 140 }}>Channel</th>
                        <th className="text-end" style={{ width: 90 }}>
                          Items
                        </th>
                        <th className="text-end" style={{ width: 150 }}>
                          ยอดสุทธิ
                        </th>
                        <th className="text-end" style={{ width: 120 }}>
                          จัดการ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={9} className="text-center text-muted py-4">
                            กำลังโหลดรายการบิล...
                          </td>
                        </tr>
                      ) : items.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center text-muted py-4">
                            ไม่พบบิลที่ต้องการ
                          </td>
                        </tr>
                      ) : (
                        items.map((item) => {
                          const isSaving = savingBillNo === item.billNo;
                          const isSelected = selectedBillNos.includes(item.billNo);
                          return (
                            <tr key={item.billNo}>
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    setSelectedBillNos((prev) =>
                                      e.target.checked
                                        ? [...prev, item.billNo]
                                        : prev.filter((billNo) => billNo !== item.billNo)
                                    );
                                  }}
                                  aria-label={`เลือกบิล ${item.billNo}`}
                                />
                              </td>
                              <td className="fw-semibold">#{item.billNo}</td>
                              <td>{formatBEDisplayDate(item.recDateBE)}</td>
                              <td>
                                <DateInput
                                  value={dateDrafts[item.billNo] ?? item.recDateBE ?? ""}
                                  onChange={(value) =>
                                    setDateDrafts((prev) => ({
                                      ...prev,
                                      [item.billNo]: value,
                                    }))
                                  }
                                />
                              </td>
                              <td>{item.paymentMethod ?? "-"}</td>
                              <td>{item.saleChannel ?? "-"}</td>
                              <td className="text-end">{item.totalItems}</td>
                              <td className="text-end">
                                {formatCurrency(item.afterDiscount ?? item.orderTotal)}
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  disabled={isSaving}
                                  onClick={() => void handleSave(item)}
                                >
                                  {isSaving ? "กำลังบันทึก..." : "บันทึก"}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mt-3">
                  <div className="small text-muted">
                    หน้า {currentPage} / {totalPages}
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={loading || currentPage <= 1}
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    >
                      ก่อนหน้า
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={loading || currentPage >= totalPages}
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    >
                      ถัดไป
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
